<template>
  <div v-if="showDebug" class="pwa-debug">
    <v-card class="debug-card" elevation="4">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-bug</v-icon>
        PWA Debug Info
        <v-spacer></v-spacer>
        <v-btn icon @click="showDebug = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-list density="compact">
          <v-list-item>
            <v-list-item-title>Service Worker</v-list-item-title>
            <v-list-item-subtitle>
              {{ debugInfo.serviceWorker ? '✅ Available' : '❌ Not Available' }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item>
            <v-list-item-title>Manifest</v-list-item-title>
            <v-list-item-subtitle>
              {{ debugInfo.manifest ? '✅ Available' : '❌ Not Available' }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item>
            <v-list-item-title>HTTPS</v-list-item-title>
            <v-list-item-subtitle>
              {{ debugInfo.https ? '✅ Secure' : '❌ Not Secure' }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item>
            <v-list-item-title>Install Prompt</v-list-item-title>
            <v-list-item-subtitle>
              {{ debugInfo.installPrompt ? '✅ Available' : '❌ Not Available' }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item>
            <v-list-item-title>App Installed</v-list-item-title>
            <v-list-item-subtitle>
              {{ debugInfo.installed ? '✅ Installed' : '❌ Not Installed' }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item>
            <v-list-item-title>Browser</v-list-item-title>
            <v-list-item-subtitle>{{ debugInfo.browser }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>

        <v-divider class="my-4" />

        <div class="text-center">
          <v-btn
            color="primary"
            variant="outlined"
            @click="refreshDebug"
            :loading="refreshing"
            size="small"
          >
            Refresh Debug Info
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const showDebug = ref(false)
const refreshing = ref(false)
const debugInfo = ref({
  serviceWorker: false,
  manifest: false,
  https: false,
  installPrompt: false,
  installed: false,
  browser: '',
})

const refreshDebug = () => {
  refreshing.value = true

  debugInfo.value.serviceWorker = 'serviceWorker' in navigator
  debugInfo.value.manifest = !!document.querySelector('link[rel="manifest"]')
  debugInfo.value.https =
    location.protocol === 'https:' || location.hostname === 'localhost'
  debugInfo.value.installPrompt = 'onbeforeinstallprompt' in window
  debugInfo.value.installed =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true

  const userAgent = navigator.userAgent
  if (userAgent.includes('Chrome')) {
    debugInfo.value.browser = 'Chrome'
  } else if (userAgent.includes('Firefox')) {
    debugInfo.value.browser = 'Firefox'
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    debugInfo.value.browser = 'Safari'
  } else if (userAgent.includes('Edge')) {
    debugInfo.value.browser = 'Edge'
  } else {
    debugInfo.value.browser = 'Unknown'
  }

  setTimeout(() => {
    refreshing.value = false
  }, 1000)
}

function handleKeyDown(e: KeyboardEvent) {
  if (e.ctrlKey && e.shiftKey && e.key === 'D') {
    showDebug.value = !showDebug.value
    if (showDebug.value) {
      refreshDebug()
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
  refreshDebug()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped>
.pwa-debug {
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 9999;
  max-width: 400px;
}

.debug-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}
</style>
